import React from 'react';
import PropTypes from 'prop-types';
import { Navigate, useParams } from 'react-router';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withStyles } from '@mui/styles';
import convertRegion, { stateCodes, TO_NAME } from '../../utils/usaStatesAPI';
import { ByStateTitle } from '../TableTitles';
import { fetchPackagesByStateForCurrentEditionRequested } from '../../stores/packages/actions';
import { addWatchlist, removeWatchlist, updateWatchlist } from '../../stores/watchlist/actions';
import { createByStatePackagesSelectorsForTable } from '../../selectors/packages';
import { getEdition } from '../../selectors/edition';
import TablesPropTypes from '../../propTypes/tables';
import BrowseTable from '../BrowseTable';
import styles from './styles';
import ApiPropTypes from '../../propTypes/api';
import { downloadMultiplePackages, downloadPackage } from '../../stores/download/actions';
import { getAvailableUnderwritersForDataTableFilter } from '../../selectors/underwriters';

class BrowseByState extends React.Component {
    static propTypes = {
        classes: PropTypes.object.isRequired,
        tableData: TablesPropTypes.tablePageData,
        tableContext: ApiPropTypes.apiPageContext.isRequired,
        hasFilters: PropTypes.bool,
        loadingUpdate: PropTypes.arrayOf(PropTypes.number).isRequired,
        onAddWatchlistRequested: PropTypes.func.isRequired,
        onRemoveWatchlistRequested: PropTypes.func.isRequired,
        onUpdateWatchlistRequested: PropTypes.func.isRequired,
        onDownloadRequested: PropTypes.func.isRequired,
        onFetchPackagesByStateRequested: PropTypes.func.isRequired,
        availableUnderwritersForDataTableFilter: PropTypes.arrayOf(PropTypes.object),
        stateCode: PropTypes.string.isRequired,
        edition: PropTypes.string.isRequired,
    };

    static defaultProps = {
        hasFilters: false,
        availableUnderwritersForDataTableFilter: [],
    };

    componentDidMount () {
        if (this.props.stateCode) {
            this.props.onFetchPackagesByStateRequested(this.props.stateCode);
        }
    }

    componentDidUpdate (prevProps) {
        /*
         * Refetch when an input that determines the data changes: the "Show Files for:"
         * edition toggle, or the selected state (when React Router reuses this component
         * across a param-only route change rather than remounting).
         */
        const editionChanged = this.props.edition !== prevProps.edition;
        const stateChanged = this.props.stateCode !== prevProps.stateCode;
        if (this.props.stateCode && (editionChanged || stateChanged)) {
            this.props.onFetchPackagesByStateRequested(this.props.stateCode);
        }
    }

    render () {
        const {
            tableData,
            tableContext,
            hasFilters,
            loadingUpdate,
            onAddWatchlistRequested,
            onRemoveWatchlistRequested,
            onUpdateWatchlistRequested,
            edition,
            onDownloadRequested,
            onFetchPackagesByStateRequested: onFetchDataRequested,
            classes,
            availableUnderwritersForDataTableFilter,
            stateCode,
        } = this.props;

        if (!stateCode || !stateCodes.includes(stateCode)) {
            return <Navigate to="/portal/browseAll" />;
        }

        const tableProps = {
            tableData,
            tableContext,
            hasFilters,
            loadingUpdate,
            onAddWatchlistRequested,
            onRemoveWatchlistRequested,
            onUpdateWatchlistRequested,
            onDownloadRequested,
            onFetchDataRequested: (context) => onFetchDataRequested(stateCode, context),
            availableUnderwritersForDataTableFilter,
        };

        return (
            <div className={classes.container}>
                <BrowseTable
                    {...tableProps}
                    edition={edition}
                    displayTypeColumn
                    displayStatesColumn={false}
                    emptyText={
                        `There are no ${convertRegion(stateCode, TO_NAME)} Packages.`
                    }
                    title={<ByStateTitle stateName={convertRegion(stateCode, TO_NAME)} />}
                />
            </div>
        );
    }
}

const createMapStateToProps = () => {
    const {
        getTableDataForState,
        getTableContextForState,
        doesTableHaveFilters,
    } = createByStatePackagesSelectorsForTable(
        (state, props) => props.stateCode,
    );
    return (state, props) => {
        return {
            edition: getEdition(state),
            tableData: getTableDataForState(state, props),
            tableContext: getTableContextForState(state, props),
            hasFilters: doesTableHaveFilters(state, props),
            loadingUpdate: state.watchlist.loadingUpdate,
            availableUnderwritersForDataTableFilter: getAvailableUnderwritersForDataTableFilter(state),
        };
    };
};

const mapDispatchToProps = function (dispatch, props) {
    return {
        onAddWatchlistRequested: (packageIds) => dispatch(addWatchlist(packageIds, false)),
        onRemoveWatchlistRequested: (packageIds) => dispatch(removeWatchlist(packageIds, false)),
        onUpdateWatchlistRequested: (...args) => dispatch(updateWatchlist(...args)),
        onDownloadRequested: (packageIds) => {
            return packageIds && packageIds.length > 1
                ? dispatch(downloadMultiplePackages(packageIds))
                : dispatch(downloadPackage(packageIds[0]));
        },
        onFetchPackagesByStateRequested: (stateCode, context = {}) => {
            dispatch(fetchPackagesByStateForCurrentEditionRequested(stateCode || props.stateCode, context));
        },
    };
};

const BrowseByStateWithStyles = compose(
    connect(createMapStateToProps, mapDispatchToProps),
    withStyles(styles),
)(BrowseByState);

const BrowseByStateFunction = (props) => {
    const params = useParams();
    return <BrowseByStateWithStyles stateCode={params.stateCode} {...props} />;
};

export default BrowseByStateFunction;
